import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { Calendar, User, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import AuthorBio from "@/components/AuthorBio";
import RelatedPosts from "@/components/RelatedPosts";
import { JsonLd, articleSchema } from "@/components/StructuredData";
import { Skeleton } from "@/components/ui/skeleton";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return ""; }
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="container-narrow py-16 space-y-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-12 w-3/4" />
        <Skeleton className="h-4 w-1/3" />
        <div className="space-y-3 mt-6">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-4/5" />
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="container-narrow py-24 text-center">
        <p className="text-foreground/70 mb-4">This article could not be found.</p>
        <button onClick={() => navigate("/blog")} className="text-primary underline">Back to blog</button>
      </div>
    );
  }

  return (
    <>
      <Seo
        title={post.title}
        description={post.meta_description || post.excerpt}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.cover_image}
      />
      <JsonLd data={articleSchema(post)} />
      <Breadcrumbs
        items={[
          { name: "Blog", path: "/blog" },
          { name: post.title, path: `/blog/${post.slug}` },
        ]}
      />

      <article className="container-narrow pt-10 pb-24" data-testid="blog-post">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-primary mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to blog
        </Link>

        <div className="text-xs uppercase tracking-widest text-accent mb-3">{post.category}</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-foreground leading-tight" data-testid="post-title">
          {post.title}
        </h1>
        <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {post.author}</span>
          <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {formatDate(post.created_at)}</span>
        </div>

        {post.cover_image ? (
          <img
            src={post.cover_image}
            alt={post.title}
            className="mt-8 w-full rounded-xl object-cover max-h-[440px]"
            loading="eager"
          />
        ) : null}

        <div
          className="prose-clinic mt-10"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
        />

        {post.tags?.length ? (
          <div className="mt-10 flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span key={t} className="text-xs rounded-full border border-border px-3 py-1 text-muted-foreground">
                #{t}
              </span>
            ))}
          </div>
        ) : null}

        <div className="mt-14 rounded-2xl border border-primary/20 bg-primary/5 p-6 sm:p-8">
          <div className="text-xs uppercase tracking-widest text-primary mb-2">Consult in Noida</div>
          <div className="font-serif text-2xl mb-2">Discuss your eye health with Dr. Gurkiran Kaur.</div>
          <p className="text-sm text-foreground/70 mb-4">Sector 32, Wave City Centre, Noida.</p>
          <Link
            to="/contact"
            className="inline-flex items-center rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-primary/90"
            data-testid="post-book-cta"
          >
            Book an Appointment
          </Link>
        </div>

        <AuthorBio />
        <RelatedPosts category={post.category} currentSlug={post.slug} />
      </article>
    </>
  );
}
